import React, { useState, useEffect } from 'react';

// Palette / fonts cohérents avec BoutiqueInfluenceurs
const BG = '#070707';
const SURFACE = '#0e0e0e';
const BORDER = 'rgba(212, 175, 55, 0.18)';
const BORDER_STRONG = 'rgba(212, 175, 55, 0.45)';
const TEXT = '#ece6d3';
const TEXT_MUTED = '#9a9485';
const GOLD = '#d4af37';
const GOLD_LIGHT = '#e8b842';
const GOLD_DARK = '#8a6916';

const FONTS_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,500;0,6..96,600;0,6..96,700;0,6..96,800&family=Inter:wght@300;400;500;600;700;800&display=swap');`;

const STORAGE_KEY = 'eshops_cookie_consent';

const CATEGORIES = [
  {
    id: 'essential',
    title: 'Strictement nécessaires',
    description: "Cookies indispensables au fonctionnement du site (navigation, panier, sécurité). Ne peuvent pas être désactivés.",
    locked: true
  },
  {
    id: 'functional',
    title: 'Fonctionnels',
    description: "Améliorent votre expérience : préférences de langue, dernier filtre utilisé, mémorisation du formulaire de commande."
  },
  {
    id: 'analytics',
    title: 'Analytiques',
    description: "Mesurent l'audience de manière anonyme pour comprendre l'usage du site et améliorer nos services. Aucune donnée personnelle n'est exploitée à des fins publicitaires externes."
  },
  {
    id: 'marketing',
    title: 'Marketing',
    description: "Permettent d'afficher des messages personnalisés et de mesurer la performance de nos campagnes (réseaux sociaux, partenaires)."
  }
];

function ConsentPreferences() {
  const [prefs, setPrefs] = useState({
    essential: true,
    functional: true,
    analytics: true,
    marketing: false
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setPrefs(p => ({ ...p, ...parsed, essential: true }));
      }
    } catch {}
  }, []);

  const toggle = (id) => {
    if (id === 'essential') return;
    setPrefs(p => ({ ...p, [id]: !p[id] }));
    setSaved(false);
  };

  const acceptAll = () => {
    setPrefs({ essential: true, functional: true, analytics: true, marketing: true });
    setSaved(false);
  };

  const refuseAll = () => {
    setPrefs({ essential: true, functional: false, analytics: false, marketing: false });
    setSaved(false);
  };

  const save = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch {}
  };

  return (
    <div style={{
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      minHeight: '100vh',
      color: TEXT,
      background: `radial-gradient(ellipse at 20% 10%, rgba(212,175,55,0.08) 0%, transparent 45%), ${BG}`,
      overflowX: 'hidden'
    }}>
      <style>{FONTS_IMPORT}</style>

      {/* Header */}
      <header style={{
        padding: 'clamp(50px, 7vw, 90px) 20px clamp(28px, 4vw, 48px)',
        textAlign: 'center',
        borderBottom: `1px solid ${BORDER}`
      }}>
        <a href="/boutique-influenceurs" style={{
          display: 'inline-block',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: GOLD_LIGHT,
          textDecoration: 'none',
          marginBottom: 24,
          opacity: 0.85
        }}>← Retour</a>
        <h1 style={{
          fontFamily: "'Bodoni Moda', 'Didot', serif",
          fontSize: 'clamp(32px, 5vw, 56px)',
          fontWeight: 700,
          letterSpacing: '-0.5px',
          textTransform: 'uppercase',
          color: '#ffffff',
          margin: '0 0 16px',
          lineHeight: 1.1
        }}>
          Consent Preferences
        </h1>
        <div style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: TEXT_MUTED,
          maxWidth: 620,
          margin: '0 auto',
          lineHeight: 1.8
        }}>
          Gérez vos préférences de cookies et de confidentialité. Vos choix sont enregistrés localement et appliqués immédiatement.
        </div>
        <div style={{
          width: 80,
          height: 1,
          background: `linear-gradient(90deg, transparent 0%, ${BORDER_STRONG} 50%, transparent 100%)`,
          margin: '24px auto 0'
        }}/>
      </header>

      {/* Body */}
      <main style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'clamp(40px, 6vw, 72px) 20px'
      }}>
        {/* Actions globales */}
        <div style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginBottom: 36
        }}>
          <button onClick={refuseAll} style={{
            background: 'transparent',
            border: `1px solid ${BORDER_STRONG}`,
            color: '#ffffff',
            padding: '12px 24px',
            borderRadius: 999,
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all .25s ease'
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            Tout refuser
          </button>
          <button onClick={acceptAll} style={{
            background: `linear-gradient(110deg, ${GOLD_DARK} 0%, ${GOLD} 50%, ${GOLD_DARK} 100%)`,
            backgroundSize: '200% auto',
            border: 'none',
            color: '#1a1407',
            padding: '12px 24px',
            borderRadius: 999,
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            boxShadow: '0 8px 22px rgba(212,175,55,0.25)'
          }}>
            Tout accepter
          </button>
        </div>

        {/* Catégories */}
        {CATEGORIES.map((cat, i) => (
          <div key={cat.id} style={{
            background: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 14,
            padding: 'clamp(18px, 2.4vw, 26px)',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 18,
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: '1 1 280px', minWidth: 0 }}>
              <h3 style={{
                fontFamily: "'Bodoni Moda', 'Didot', serif",
                fontSize: 'clamp(15px, 1.7vw, 18px)',
                fontWeight: 700,
                letterSpacing: '-0.2px',
                textTransform: 'uppercase',
                color: '#ffffff',
                margin: '0 0 8px',
                display: 'flex',
                alignItems: 'baseline',
                gap: 12
              }}>
                <span style={{ color: GOLD_LIGHT, fontSize: '0.65em', fontWeight: 600, letterSpacing: 2 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>{cat.title}</span>
                {cat.locked && (
                  <span style={{
                    fontSize: 9,
                    fontWeight: 500,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: GOLD_LIGHT,
                    border: `1px solid ${BORDER_STRONG}`,
                    borderRadius: 999,
                    padding: '2px 8px'
                  }}>Toujours actif</span>
                )}
              </h3>
              <p style={{
                fontSize: 'clamp(12px, 1.3vw, 14px)',
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#d8d2c0',
                margin: 0
              }}>{cat.description}</p>
            </div>

            {/* Toggle */}
            <button
              type="button"
              onClick={() => toggle(cat.id)}
              disabled={cat.locked}
              aria-label={`Toggle ${cat.title}`}
              style={{
                position: 'relative',
                width: 52,
                height: 28,
                borderRadius: 999,
                border: `1px solid ${prefs[cat.id] ? BORDER_STRONG : BORDER}`,
                background: prefs[cat.id]
                  ? `linear-gradient(110deg, ${GOLD_DARK} 0%, ${GOLD} 50%, ${GOLD_DARK} 100%)`
                  : 'rgba(255,255,255,0.06)',
                cursor: cat.locked ? 'not-allowed' : 'pointer',
                opacity: cat.locked ? 0.6 : 1,
                transition: 'all .25s ease',
                flexShrink: 0,
                padding: 0,
                outline: 'none'
              }}>
              <span style={{
                position: 'absolute',
                top: 2,
                left: prefs[cat.id] ? 26 : 2,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: prefs[cat.id] ? '#fff' : '#888',
                transition: 'left .25s ease',
                boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
              }}/>
            </button>
          </div>
        ))}

        {/* Save button */}
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <button onClick={save} style={{
            background: 'transparent',
            border: `1px solid ${BORDER_STRONG}`,
            color: GOLD_LIGHT,
            padding: '14px 36px',
            borderRadius: 999,
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: 2.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all .25s ease'
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Enregistrer mes préférences
          </button>
          {saved && (
            <div style={{
              marginTop: 16,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: GOLD_LIGHT
            }}>
              ✓ Vos préférences ont été enregistrées
            </div>
          )}
        </div>
      </main>

      {/* Footer mini */}
      <footer style={{
        padding: '36px 20px',
        textAlign: 'center',
        borderTop: `1px solid ${BORDER}`,
        fontSize: 11,
        fontWeight: 400,
        letterSpacing: 2,
        textTransform: 'uppercase',
        color: TEXT_MUTED
      }}>
        <a href="https://www.e-shop-pro.fr/" target="_blank" rel="noopener noreferrer" style={{
          fontFamily: "'Bodoni Moda', serif",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 2,
          color: '#ffffff',
          textDecoration: 'none',
          marginBottom: 8,
          display: 'inline-block'
        }}>E SHOPS PRO</a>
        <div style={{ marginTop: 10, opacity: 0.85 }}>
          All rights reserved — Copyright © {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

export default ConsentPreferences;
